/**
 * Abschnitt und Zeile der Einstellungsseite — aus `ui_kits/dashboard/SettingsScreen.jsx`
 * (HM-162).
 *
 * Ein Abschnitt ist eine Überschrift **über** einer Karte ohne Innenabstand; die
 * Zeilen darin tragen links die Beschriftung samt Hinweis und rechts das Feld,
 * getrennt durch eine feine Linie. Schmal rückt die Beschriftung über das Feld.
 *
 * **Außerhalb der Seite definiert**, nicht in ihr: Eine Komponente, die bei
 * jedem Zeichnen neu entsteht, baut ihre Felder bei jedem Tastendruck neu auf,
 * und der Fokus geht verloren.
 */
import type { ReactNode } from "react";
import { Card, useNarrow } from "@hausmeter/ui";

export function SettingsSection({
  title,
  subtitle,
  children,
}: {
  readonly title: string;
  readonly subtitle?: string;
  readonly children: ReactNode;
}) {
  return (
    <section style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <div>
        <h2 style={{ margin: 0, fontFamily: "var(--font-display)", fontSize: 15, fontWeight: 700, color: "var(--text-strong)" }}>
          {title}
        </h2>
        {subtitle !== undefined && <div style={{ fontSize: 12.5, color: "var(--text-muted)" }}>{subtitle}</div>}
      </div>
      <Card padding="none" style={{ overflow: "visible" }}>
        {children}
      </Card>
    </section>
  );
}

/** Die Breite, unter der die Beschriftung über das Feld rückt — die des Entwurfs. */
const NARROW = 760;

export function FieldRow({
  label,
  hint,
  last = false,
  children,
}: {
  readonly label: string;
  readonly hint?: string | null;
  readonly last?: boolean;
  readonly children: ReactNode;
}) {
  const narrow = useNarrow(NARROW);
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: narrow ? "minmax(0,1fr)" : "220px minmax(0,1fr)",
        gap: narrow ? 8 : 24,
        padding: "16px 20px",
        borderBottom: last ? 0 : "1px solid var(--border-subtle)",
      }}
    >
      <div style={{ paddingTop: narrow ? 0 : 9 }}>
        <div style={{ fontSize: 13.5, fontWeight: 600, color: "var(--text-strong)" }}>{label}</div>
        {hint !== undefined && hint !== null && (
          <div style={{ fontSize: 12, color: "var(--text-muted)", lineHeight: 1.45, marginTop: 2 }}>{hint}</div>
        )}
      </div>
      <div style={{ minWidth: 0 }}>{children}</div>
    </div>
  );
}

/** Eine Zeile mit Kreis aus Initialen, Name und Adresse — der Zugriff im Entwurf. */
export function PersonLine({
  name,
  detail,
  initials,
  children,
}: {
  readonly name: string;
  readonly detail?: string;
  readonly initials: string;
  readonly children?: ReactNode;
}) {
  return (
    <div
      style={{
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        gap: 12,
        padding: "14px 20px",
        borderBottom: "1px solid var(--border-subtle)",
      }}
    >
      <span
        aria-hidden="true"
        style={{
          width: 34,
          height: 34,
          flexShrink: 0,
          borderRadius: 99,
          background: "var(--surface-sunken)",
          color: "var(--text-body)",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 12.5,
          fontWeight: 700,
        }}
      >
        {initials}
      </span>
      <span style={{ flex: "1 1 200px", minWidth: 0, display: "flex", flexDirection: "column" }}>
        <span style={{ fontSize: 13.5, fontWeight: 600, color: "var(--text-strong)", overflowWrap: "anywhere" }}>{name}</span>
        {detail !== undefined && (
          <span style={{ fontSize: 12, color: "var(--text-muted)", overflowWrap: "anywhere" }}>{detail}</span>
        )}
      </span>
      {children !== undefined && (
        <span style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>{children}</span>
      )}
    </div>
  );
}
